// QueryBuilder.jsx

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogTrigger,
  AlertDialogCancel,
  AlertDialogDescription,
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Plus } from "lucide-react";
import IconButton from "../../../components/IconButton";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MultiSelect } from "../../../components/ui/multiselect";
import SelectInput from "../../../components/SelectInput";
import { useDispatch, useSelector } from "react-redux";
import { getColumnListValues, getFieldValueOptions, resetQueryBuilder, setQBErrors } from "../../../store/extractions/action";
import { setOpenModal } from "../../../store/extDataGroups/actions";
import {
  getAvailableFields,
  isSelectField,
  addConditionToGroup,
  addNewGroup,
  removeConditionFromGroup,
  removeGroup,
  updateCondition,
  updateGroupOperator,
  buildWhereClause,
  getInitialState,
  resetState,
  prepareOutputPayload,
  validateQueryBuilder,
  hasValidationErrors,
  getErrorFromPath,
  getInputType,
  stripHtmlTags,
} from "../../../commonUtils/queryBuilder.helper";
import { MultiEntryInput } from "../../../components/MultiEntryInput";
import { toast } from "react-toastify";

const QueryBuilder = ({ 
  options = [],
  fieldDropdownOptions = {},
  triggerText = "Add New",
  titleText = "Add New",
  nameLabel = "Group Name",
  namePlaceholder = "",
  onSave,
  editMode = false,
  data,
  openModal,
  createAction = () => {},
  updateAction = () => {},
  disabled = false,
  saveInProgress = false
}) => {
  const dispatch = useDispatch();
  
  const {
    extractionData,
    qbErrors 
  } = useSelector((state) => state.Extractions);

  const [queryName, setQueryName] = useState("");
  const [conditionGroups, setConditionGroups] = useState(getInitialState().conditionGroups);
  const [fieldOptionsCache, setFieldOptionsCache] = useState({});

  const { editDataId, editDataTitle, editDataFields } = useMemo(() => ({
    editDataId: data?._id,
    editDataTitle: data?.title || "",
    editDataFields: Array.isArray(data?.fields) ? data.fields : []
  }), [data?._id, data?.title, data?.fields]);
  
  const hasLoadedDataRef = useRef(false);
  const currentEditIdRef = useRef(null);

  // Check if a condition is the first row of the first group
  // const isFirstRowOfFirstGroup = useCallback((groupId, condIdx) => {
  //   return groupId === 1 && condIdx === 0;
  // }, []);

  // Check if this is the only condition in the first group
  const isOnlyConditionInFirstGroup = useCallback((groupId, groupConditions) => {
    return groupId === 1 && groupConditions.length === 1;
  }, []);

  // Load column list values on mount
  useEffect(() => {
    if (!options || options.length === 0) {
      dispatch(getColumnListValues());
    }
  }, [dispatch, options]);

  // Process fieldDropdownOptions and update cache
  useEffect(() => {
    if (!fieldDropdownOptions || Object.keys(fieldDropdownOptions).length === 0) {
      return;
    }

    Object.entries(fieldDropdownOptions).forEach(([fieldName, fieldData]) => {
      if (fieldData.loading) {
        return;
      }

      if (fieldData.values && fieldData.values.length > 0) {
        const processedOptions = fieldData.values.map(value => {
          const cleanValue = stripHtmlTags(value);
          return {
            value: cleanValue,
            label: cleanValue
          };
        });

        setFieldOptionsCache(prev => ({
          ...prev,
          [fieldName]: processedOptions
        }));
      } else if (!fieldData.error) {
        setFieldOptionsCache(prev => ({
          ...prev,
          [fieldName]: []
        }));
      }
    });
  }, [fieldDropdownOptions]);

  const handleReset = useCallback(() => {
    try {
      const initialState = resetState();
      setQueryName(initialState.queryName);
      setConditionGroups(initialState.conditionGroups);
      setFieldOptionsCache(initialState.fieldOptionsCache);
      dispatch(resetQueryBuilder());
      dispatch(setQBErrors({}));
    } catch (error) {
      console.error("Error resetting query builder:", error);
    }
  }, [dispatch]);

  // Load data when modal opens
  useEffect(() => {    
    if (!openModal) {
      return;
    }

    if (editMode && editDataId) {
      const isDifferentItem = currentEditIdRef.current !== editDataId;
      
      if (!isDifferentItem && hasLoadedDataRef.current) {
        return;
      }

      currentEditIdRef.current = editDataId;
      hasLoadedDataRef.current = true;
      
      setQueryName(editDataTitle);
      
      if (editDataFields.length > 0) {
        const fieldsNeedingOptions = new Set();
        editDataFields.forEach(group => {
          group.data.forEach(condition => {
            if (condition.field && isSelectField(condition.field, options)) {
              fieldsNeedingOptions.add(condition.field);
            }
          });
        });

        if (fieldsNeedingOptions.size > 0) {
          Array.from(fieldsNeedingOptions).forEach(field => {
            dispatch(getFieldValueOptions({ field, keyword: "" }));
          });
        }

        const transformedGroups = editDataFields.map((group, groupIdx) => ({
          id: groupIdx + 1,
          groupOperator: groupIdx === 0 ? null : (group.connector || "OR"),
          conditions: group.data.map((condition, condIdx) => {
            let value;
            const operatorNeedsArray = ['IN', 'NOT IN', 'BETWEEN', 'NOT BETWEEN', 'LIKE', 'NOT LIKE', 'ILIKE', 'NOT ILIKE'].includes(condition.operator);
            
            if (!condition.values || condition.values.length === 0) {
              value = operatorNeedsArray ? [] : '';
            } else if (operatorNeedsArray) {
              value = Array.isArray(condition.values) ? condition.values : [condition.values];
            } else if (['IS NULL', 'IS NOT NULL'].includes(condition.operator)) {
              value = '';
            } else {
              value = Array.isArray(condition.values) ? (condition.values[0] || '') : condition.values;
            }
            
            return {
              andOr: condIdx === 0 ? "AND" : (condition.connector || "AND"),
              field: condition.field,
              operator: condition.operator,
              value: value
            };
          })
        }));

        setConditionGroups(transformedGroups);
      }
    } else if (!editMode) {
      if (!hasLoadedDataRef.current) {
        handleReset();
        hasLoadedDataRef.current = true;
      }
    }
  }, [openModal, editMode, editDataId, editDataTitle, editDataFields, options, dispatch, handleReset]);

  // Reset when modal closes
  useEffect(() => {
    if (!openModal) {
      hasLoadedDataRef.current = false;
      currentEditIdRef.current = null;
    }
  }, [openModal]);

  // Get available fields
  const availableFields = useMemo(() => 
    getAvailableFields(options), 
    [options]
  );

  // Fetch options for a specific field
  const fetchFieldOptions = useCallback((fieldName, keyword = "") => {
    if (isSelectField(fieldName, options)) {
      dispatch(getFieldValueOptions({ field: fieldName, keyword: keyword }));
    }
  }, [options, dispatch]);

  // Get options for a field from cache
  const getOptionsForField = useCallback((fieldName) => {
    const options = fieldOptionsCache[fieldName] || [];
    return options;
  }, [fieldOptionsCache]);

  // Check if field is loading
  const isFieldLoading = useCallback((fieldName) => {
    return fieldDropdownOptions[fieldName]?.loading || false;
  }, [fieldDropdownOptions]);

  // Handler functions
  const handleAddCondition = useCallback((groupId) => {
    setConditionGroups(groups => addConditionToGroup(groups, groupId));
  }, []);

  const handleAddGroup = useCallback(() => {
    setConditionGroups(groups => addNewGroup(groups));
  }, []);

  const handleRemoveCondition = useCallback((groupId, conditionIndex) => {
    // Find the group to check its conditions
    const group = conditionGroups.find(g => g.id === groupId);
    
    // CRITICAL: Prevent removing if this is the only condition in the first group
    if (isOnlyConditionInFirstGroup(groupId, group?.conditions || [])) {
      toast.warning("The first group must have at least one condition.");
      return;
    }

    setConditionGroups(groups => 
      removeConditionFromGroup(groups, groupId, conditionIndex)
    );
  }, [conditionGroups, isOnlyConditionInFirstGroup]);

  const handleRemoveGroup = useCallback((groupId) => {
    setConditionGroups(groups => removeGroup(groups, groupId));
  }, []);

  const handleConditionChange = useCallback((groupId, conditionIndex, field, value) => {
    setConditionGroups(groups => {
      const newGroups = updateCondition(groups, groupId, conditionIndex, field, value, options);
      
      if (field === 'operator') {
        const group = newGroups.find(g => g.id === groupId);
        const condition = group?.conditions[conditionIndex];
        
        if (condition) {
          const inputType = getInputType(condition, options);
          
          if (inputType === 'null') {
            condition.value = '';
          } else if (['multiselect', 'multientry', 'between'].includes(inputType)) {
            condition.value = Array.isArray(condition.value) ? condition.value : [];
          } else {
            condition.value = Array.isArray(condition.value) ? '' : condition.value;
          }
        }
      }
      
      // Fetch options when field changes
      if (field === 'field' && isSelectField(value, options)) {
        fetchFieldOptions(value);
      }
      
      return newGroups;
    });
  }, [options, fetchFieldOptions]);

  const handleGroupOperatorChange = useCallback((groupId, operator) => {
    setConditionGroups(groups => updateGroupOperator(groups, groupId, operator));
  }, []);

  const handleSave = useCallback(() => {
    dispatch(setQBErrors({}));
    
    const errors = validateQueryBuilder(queryName, conditionGroups);
    dispatch(setQBErrors(errors));

    if (hasValidationErrors(errors)) {
      toast.error("Please fix all validation errors before saving.");
      return;
    }

    if (!extractionData?.id) {
      toast.error("Extraction not found. Please refresh the page.");
      return;
    }

    const whereClause = buildWhereClause(conditionGroups);
    const payload = prepareOutputPayload(queryName, conditionGroups, whereClause, options);
    
    if (editMode && data?._id) {
      dispatch(updateAction({ id: data._id, formData: payload }));
    } else {
      dispatch(createAction({ formData: payload }));
    }
    
    if (onSave) {
      onSave(payload);
    }
  }, [dispatch, queryName, conditionGroups, extractionData, editMode, data, options, onSave, createAction, updateAction]);

  const handleCancel = useCallback(() => {
    dispatch(setOpenModal(false));
  }, [dispatch]);

  const handleOpenModal = useCallback(() => {
    dispatch(setOpenModal(true));
  }, [dispatch]);

  // Check if delete button should be disabled for a condition
  const isDeleteDisabled = useCallback((groupId, groupConditions) => {
    return isOnlyConditionInFirstGroup(groupId, groupConditions);
  }, [isOnlyConditionInFirstGroup]);

  // Render value field
  const renderValueField = (condition, groupId, condIdx) => {
    const inputType = getInputType(condition, options);
    const uniqueKey = `${groupId}-${condIdx}-${condition.field}-${condition.operator}`;
    const valueError = getErrorFromPath(qbErrors, `groups.${groupId}.conditions.${condIdx}.value`);
    const fieldOptions = getOptionsForField(condition.field);
    const currentValue = Array.isArray(condition.value) ? condition.value : [];
    const isLoading = isFieldLoading(condition.field);

    switch (inputType) {
      case 'null':
        return (
          <Input
            key={uniqueKey}
            value="N/A"
            disabled
            placeholder="No value needed"
            className="h-9 bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
          />
        );

      case 'between':
        return (
          <MultiSelect
            key={uniqueKey}
            options={fieldOptions}
            value={currentValue}
            onValueChange={(newValue) => {
              if (newValue.length <= 2) {
                handleConditionChange(groupId, condIdx, "value", newValue);
              }
            }}
            onSearchChange={(keyword) => fetchFieldOptions(condition.field, keyword)}
            isLoading={isLoading}
            placeholder="Select exactly 2 values..."
            className="h-9 bg-white border-gray-300"
            debounceMs={300}
            showSearch={true}
            error={valueError}
            maxSelections={2}
          />
        );

      case 'multientry':
        return (
          <MultiEntryInput
            key={uniqueKey}
            value={Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : [])}
            onValueChange={(newValue) =>
              handleConditionChange(groupId, condIdx, "value", newValue)
            }
            options={fieldOptions}
            onSearchChange={(keyword) => fetchFieldOptions(condition.field, keyword)}
            isLoading={isLoading}
            placeholder="Type or select, then press Enter..."
            className="h-9 bg-white border-gray-300"
            debounceMs={300}
            showSearch={true}
            error={valueError}
          />
        );
      
      case 'multiselect':
        return (
          <MultiSelect
            key={uniqueKey}
            options={fieldOptions}
            value={currentValue}
            onValueChange={(newValue) =>
              handleConditionChange(groupId, condIdx, "value", newValue)
            }
            onSearchChange={(keyword) => fetchFieldOptions(condition.field, keyword)}
            isLoading={isLoading}
            placeholder="Search and select..."
            className="h-9 bg-white border-gray-300"
            debounceMs={300}
            showSearch={true}
            error={valueError}
          />
        );

      case 'select':
        return (
          <SelectInput
            key={uniqueKey}
            type="map"
            list={fieldOptions}
            selectedValue={Array.isArray(condition.value) ? "" : condition.value}
            onChange={(newValue) =>
              handleConditionChange(groupId, condIdx, "value", newValue)
            }
            placeholder="Search and select..."
            className="h-9 bg-white border-gray-300"
            clearable={true}
            error={valueError}
          />
        );

      case 'input':
      default:
        return (
          <Input
            key={uniqueKey}
            value={Array.isArray(condition.value) ? "" : condition.value}
            onChange={(e) =>
              handleConditionChange(groupId, condIdx, "value", e.target.value)
            }
            placeholder="Enter value"
            className="h-9 bg-white border-gray-300"
            error={valueError}
          />
        );
    }
  };

  // Desktop condition row renderer
  const renderDesktopConditionRow = (group, condition, condIdx, groupIdx) => (
    <div key={condIdx} className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-1">
        {condIdx > 0 ? (
          <Select
            value={condition.andOr}
            onValueChange={(v) =>
              handleConditionChange(group.id, condIdx, "andOr", v)
            }
          >
            <SelectTrigger className="h-9 bg-white border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="h-9" />
        )}
      </div>

      <div className="col-span-3">
        <SelectInput
          type="map"
          list={availableFields.map(field => ({ value: field, label: field }))}
          selectedValue={condition.field}
          onChange={(v) =>
            handleConditionChange(group.id, condIdx, "field", v)
          }
          placeholder="Select Field"
          className="h-9 bg-white border-gray-300"
          clearable={false}
          error={getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`)}
        />
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`)}
          </small>
        )}
      </div>

      <div className="col-span-2">
        <Select
          value={condition.operator}
          onValueChange={(v) =>
            handleConditionChange(group.id, condIdx, "operator", v)
          }
        >
          <SelectTrigger className="h-9 bg-white border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="=">=</SelectItem>
            <SelectItem value="!=">!=</SelectItem>
            <SelectItem value=">">{'>'}</SelectItem>
            <SelectItem value=">=">{'>='}</SelectItem>
            <SelectItem value="<">{'<'}</SelectItem>
            <SelectItem value="<=">{'<='}</SelectItem>
            <SelectItem value="IN">IN</SelectItem>
            <SelectItem value="NOT IN">NOT IN</SelectItem>
            <SelectItem value="LIKE">LIKE</SelectItem>
            <SelectItem value="NOT LIKE">NOT LIKE</SelectItem>
            <SelectItem value="ILIKE">ILIKE</SelectItem>
            <SelectItem value="NOT ILIKE">NOT ILIKE</SelectItem>
            <SelectItem value="BETWEEN">BETWEEN</SelectItem>
            <SelectItem value="NOT BETWEEN">NOT BETWEEN</SelectItem>
            <SelectItem value="IS NULL">IS NULL</SelectItem>
            <SelectItem value="IS NOT NULL">IS NOT NULL</SelectItem>
          </SelectContent>
        </Select>
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.operator`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.operator`)}
          </small>
        )}
      </div>

      <div className="col-span-5">
        {renderValueField(condition, group.id, condIdx)}
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.value`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.value`)}
          </small>
        )}
      </div>

      <div className="col-span-1 flex gap-1 justify-end">
        {condIdx === group.conditions.length - 1 && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => handleAddCondition(group.id)}
            className="h-9 w-9 hover:bg-gray-200"
          >
            <Plus className="w-4 h-4 text-gray-600" />
          </Button>
        )}
        <IconButton
          variant="ghost"
          size="icon"
          Icon={XMarkIcon}
          onClick={() => handleRemoveCondition(group.id, condIdx)}
          disabled={isDeleteDisabled(group.id, group.conditions)}
        />
      </div>
    </div>
  );

  // Mobile condition card renderer
  const renderMobileConditionCard = (group, condition, condIdx, groupIdx) => (
    <div key={condIdx} className="bg-white border border-gray-200 rounded p-3">
      {/* Action buttons row */}
      <div className="flex justify-end gap-1">
        {condIdx === group.conditions.length - 1 && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => handleAddCondition(group.id)}
            className="h-9 w-9 hover:bg-gray-200"
          >
            <Plus className="w-4 h-4 text-gray-600" />
          </Button>
        )}
        <IconButton
          variant="ghost"
          size="icon"
          Icon={XMarkIcon}
          onClick={() => handleRemoveCondition(group.id, condIdx)}
          disabled={isDeleteDisabled(group.id, group.conditions)}
        />
      </div>

      {condIdx > 0 && (
        <div>
          <Label className="text-xs font-medium text-gray-600 mb-1.5 block">
            And/Or
          </Label>
          <Select 
            value={condition.andOr}
            onValueChange={(v) =>
              handleConditionChange(group.id, condIdx, "andOr", v)
            }
          >
            <SelectTrigger className="h-9 bg-white border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-xs font-medium text-gray-600 mb-1.5 block">
          Field
        </Label>
        <SelectInput
          type="map"
          list={availableFields.map(field => ({ value: field, label: field }))}
          selectedValue={condition.field}
          onChange={(v) =>
            handleConditionChange(group.id, condIdx, "field", v)
          }
          placeholder="Select Field"
          className="h-9 bg-white border-gray-300"
          clearable={false}
          error={getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`)}
        />
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.field`)}
          </small>
        )}
      </div>

      <div className="mt-2">
        <Label className="text-xs font-medium text-gray-600 mb-1.5 block">
          Operator
        </Label>
        <Select 
          value={condition.operator}
          onValueChange={(v) =>
            handleConditionChange(group.id, condIdx, "operator", v)
          }
        >
          <SelectTrigger className="h-9 bg-white border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="=">=</SelectItem>
            <SelectItem value="!=">!=</SelectItem>
            <SelectItem value=">">{'>'}</SelectItem>
            <SelectItem value=">=">{'>='}</SelectItem>
            <SelectItem value="<">{'<'}</SelectItem>
            <SelectItem value="<=">{'<='}</SelectItem>
            <SelectItem value="IN">IN</SelectItem>
            <SelectItem value="NOT IN">NOT IN</SelectItem>
            <SelectItem value="LIKE">LIKE</SelectItem>
            <SelectItem value="NOT LIKE">NOT LIKE</SelectItem>
            <SelectItem value="ILIKE">ILIKE</SelectItem>
            <SelectItem value="NOT ILIKE">NOT ILIKE</SelectItem>
            <SelectItem value="BETWEEN">BETWEEN</SelectItem>
            <SelectItem value="NOT BETWEEN">NOT BETWEEN</SelectItem>
            <SelectItem value="IS NULL">IS NULL</SelectItem>
            <SelectItem value="IS NOT NULL">IS NOT NULL</SelectItem>
          </SelectContent>
        </Select>
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.operator`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.operator`)}
          </small>
        )}
      </div>

      <div className="mt-2">
        <Label className="text-xs font-medium text-gray-600 mb-1.5 block">
          Value
        </Label>
        {renderValueField(condition, group.id, condIdx)}
        {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.value`) && (
          <small className="text-destructive text-xs mt-1 block">
            {getErrorFromPath(qbErrors, `groups.${group.id}.conditions.${condIdx}.value`)}
          </small>
        )}
      </div>

      {/* Separator line between conditions except after the last one */}
      {condIdx < group.conditions.length - 1 && (
        <div className="border-t border-gray-200 pt-3" />
      )}
    </div>
  );

  return (
    <AlertDialog open={openModal}>
      <AlertDialogTrigger asChild>
        {!editMode && (
          <Button onClick={handleOpenModal} disabled={disabled}>
            {triggerText}
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent
        className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0"
      >
        <AlertDialogHeader className="bg-primary text-white px-6 py-4 rounded-t-lg">
          <AlertDialogTitle className="text-base font-medium">{titleText}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            Build and configure your query with custom conditions and filters
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div id="query-builder-content" className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block text-[#7d8a99]">
                {nameLabel}
              </Label>
              <Input
                value={queryName}
                onChange={(e) => setQueryName(e.target.value)}
                placeholder={namePlaceholder}
                className="bg-white border-gray-300 h-10"
                error={getErrorFromPath(qbErrors, 'queryName')}
              />
              {getErrorFromPath(qbErrors, 'queryName') && (
                <small className="text-destructive text-xs mt-1 block">
                  {getErrorFromPath(qbErrors, 'queryName')}
                </small>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium mb-3 text-[#7d8a99]">Prepare your Query</h3>
              
              <div className="hidden lg:grid grid-cols-12 gap-3 mb-3 text-sm font-medium text-[#7d8a99] bg-[#e8ecef] px-4 py-2 rounded">
                <div className="col-span-1">And/Or</div>
                <div className="col-span-3">Field</div>
                <div className="col-span-2">Operator</div>
                <div className="col-span-5">Value</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              <div className="space-y-0">
                {conditionGroups.map((group, groupIdx) => (
                  <div key={group.id}>
                    <div className="flex justify-between items-center gap-2 rounded mt-2 w-full">
                      <div className="bg-[#d4dce3] w-[100px] px-3 py-1 rounded-t-lg">
                        {group.groupOperator ? (
                          <Select
                            value={group.groupOperator}
                            onValueChange={(v) => handleGroupOperatorChange(group.id, v)}
                          >
                            <SelectTrigger className="w-16 h-8 bg-[#d4dce3] border-none text-sm font-medium">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">AND</SelectItem>
                              <SelectItem value="OR">OR</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="h-8" />
                        )}
                      </div>
                      <div className="bg-[#d4dce3] px-3 py-1 rounded-t-lg flex gap-1">
                        <IconButton
                          variant="ghost"
                          size="icon"
                          Icon={Plus}
                          onClick={handleAddGroup}
                          />
                        <IconButton
                          variant="ghost"
                          size="icon"
                          Icon={XMarkIcon}
                          onClick={() => handleRemoveGroup(group.id)}
                          disabled={groupIdx === 0}
                        />
                      </div>
                    </div>

                    {/* Conditions Container */}
                    <div className="bg-gray-50 border border-gray-200 rounded px-2 sm:px-4 py-2 sm:py-3">
                      {/* Desktop View */}
                      <div className="hidden lg:block space-y-2">
                        {group.conditions.map((condition, condIdx) => 
                          renderDesktopConditionRow(group, condition, condIdx, groupIdx)
                        )}
                      </div>

                      {/* Mobile View */}
                      <div className="lg:hidden space-y-3">
                        {group.conditions.map((condition, condIdx) => 
                          renderMobileConditionCard(group, condition, condIdx, groupIdx)
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className={`p-4 rounded text-sm font-mono text-gray-900 ${
                qbErrors?.queryError ? 'bg-red-50 border border-red-400' : 'bg-accent'
              }`}>
                {buildWhereClause(conditionGroups)}
              </div>
              {qbErrors?.queryError && (
                <small className="text-destructive text-xs mt-1 block">
                  {qbErrors.queryError}
                </small>
              )}
            </div>
          </div>
        </div>

        <AlertDialogFooter className="border-t bg-white px-6 py-4">
          <AlertDialogCancel className="mr-2" onClick={handleCancel}>Cancel</AlertDialogCancel>
          <Button 
            onClick={handleSave} 
            disabled={editMode && !data && saveInProgress}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default QueryBuilder;